// Sales prospects: businesses the team is approaching, as opposed to leads
// (enquiries that arrive through the contact form).
import { api } from "./client";
import { emptyClient } from "./clients";

export const PROSPECT_STAGES = [
  { value: "new",        label: "To contact" },
  { value: "contacted",  label: "Contacted" },
  { value: "follow_up",  label: "Follow up" },
  { value: "interested", label: "Interested" },
  { value: "won",        label: "Won" },
  { value: "lost",       label: "Not interested" },
];

// Stages where the prospect is still being worked.
export const OPEN_STAGES = ["new", "contacted", "follow_up", "interested"];

export const PROSPECT_SOURCES = [
  "Google Maps",
  "Referral",
  "Walk-in / field visit",
  "Social media",
  "Phone directory",
  "Website",
  "Other",
];

export const emptyProspect = {
  businessName: "",
  contactName: "",
  phone: "",
  whatsapp: "",
  email: "",
  location: "",
  source: "",
  service: "",
  stage: "new",
  nextFollowUp: "",
  notes: "",
};

const TEXT_KEYS = Object.keys(emptyProspect).filter((k) => k !== "stage" && k !== "nextFollowUp");

// Trim text fields, keep stage as-is and send an empty follow-up date as null.
const pick = (data) => ({
  ...Object.fromEntries(TEXT_KEYS.map((k) => [k, String(data[k] ?? "").trim()])),
  stage: data.stage || "new",
  nextFollowUp: data.nextFollowUp || null,
});

export async function getProspects() {
  const { prospects } = await api.get("/prospects", { auth: true });
  return prospects;
}

// Returns the saved record (with id and timestamps).
export async function createProspect(data) {
  return api.post("/prospects", pick(data), { auth: true });
}

export function previewProspectCsv(data) {
  return api.post("/prospects/import-preview", data, { auth: true });
}

export function importProspectCsv(data) {
  return api.post("/prospects/import", data, { auth: true });
}

export async function updateProspect(id, data) {
  await api.put(`/prospects/${id}`, pick(data), { auth: true });
}

// Partial change (stage, follow-up date, clientId) without resending the form.
export async function patchProspect(id, fields) {
  await api.put(`/prospects/${id}`, fields, { auth: true });
}

export async function deleteProspect(id) {
  await api.del(`/prospects/${id}`, { auth: true });
}

// Today's date as YYYY-MM-DD in local time (matches <input type="date">).
export function todayISO() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// A follow-up is due when it is today or earlier and the prospect is still open.
export function isFollowUpDue(p, today = todayISO()) {
  return Boolean(p.nextFollowUp) && p.nextFollowUp <= today && OPEN_STAGES.includes(p.stage || "new");
}

// Build a client record when a prospect signs up.
export function clientFromProspect(p) {
  return {
    ...emptyClient,
    name:         p.contactName || "",
    businessName: p.businessName || "",
    phone:        p.phone || "",
    whatsapp:     p.whatsapp || p.phone || "",
    email:        p.email || "",
    address:      p.location || "",
    notes:        [p.service && `Signed up for: ${p.service}`, p.source && `Found via: ${p.source}`, p.notes]
      .filter(Boolean)
      .join("\n"),
  };
}
